import { Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FullScreenModal } from '../../components';
import { useActions } from '../../data';
import type { Transaction } from '../../data/types';
import {
  deleteReceipt,
  getReceipt,
  type StoredReceipt,
} from '../../lib/receiptStore';
import {
  TransactionForm,
  type EntryDraft,
} from '../today/TransactionForm';

type Props = {
  /** When non-null the sheet is open and editing this transaction. */
  transaction: Transaction | null;
  onClose: () => void;
  onUpdate: (id: string, patch: EntryDraft) => void;
};

/**
 * BottomSheet wrapper around TransactionForm for editing an existing
 * transaction. The form is pre-filled with the transaction's current
 * values; the NumPad's Done key reads "Update" and commits via onUpdate.
 *
 * When a receipt is attached, a thumbnail card sits above the form;
 * tapping it opens a fullscreen viewer, and "Remove" detaches the
 * receipt from the transaction and drops it from IndexedDB.
 */
export function EditTransactionSheet({ transaction, onClose, onUpdate }: Props) {
  const actions = useActions();
  const [receipt, setReceipt] = useState<StoredReceipt | null>(null);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptError, setReceiptError] = useState(false);

  // Load the receipt blob whenever the sheet swaps to a new tx.
  useEffect(() => {
    let cancelled = false;
    setReceipt(null);
    setReceiptError(false);
    setReceiptOpen(false);
    if (transaction?.receiptId) {
      getReceipt(transaction.receiptId).then((r) => {
        if (cancelled) return;
        if (r) setReceipt(r);
        else setReceiptError(true);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [transaction?.id, transaction?.receiptId]);

  const removeReceipt = async () => {
    if (!transaction) return;
    if (transaction.receiptId) await deleteReceipt(transaction.receiptId);
    actions.updateTransaction(transaction.id, { receiptId: undefined });
    setReceipt(null);
    setReceiptOpen(false);
  };

  return (
    <FullScreenModal
      open={!!transaction}
      onClose={onClose}
      title="Edit transaction"
    >
      {transaction ? (
        <>
          {transaction.receiptId ? (
            <div className="px-card-pad pt-3">
              <div className="flex items-center gap-3 rounded-card bg-surface-secondary p-2.5">
                {receipt ? (
                  <button
                    type="button"
                    onClick={() => setReceiptOpen(true)}
                    className="shrink-0"
                    aria-label="Open receipt"
                  >
                    <img
                      src={receipt.thumbB64}
                      alt="Receipt"
                      className="w-14 h-14 rounded-button object-cover"
                    />
                  </button>
                ) : (
                  <div className="shrink-0 w-14 h-14 rounded-button bg-surface inline-flex items-center justify-center text-text-tertiary text-[10px]">
                    {receiptError ? 'No img' : '…'}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-medium uppercase tracking-[0.05em] text-text-tertiary">
                    Receipt
                  </div>
                  <div className="text-[12px] text-text-secondary">
                    {receiptError
                      ? 'Receipt unavailable.'
                      : 'Tap to view full size.'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={removeReceipt}
                  aria-label="Remove receipt"
                  className="shrink-0 w-8 h-8 inline-flex items-center justify-center rounded-pill text-text-tertiary"
                >
                  <Trash2 size={16} strokeWidth={1.75} />
                </button>
              </div>
            </div>
          ) : null}

          <TransactionForm
            type={transaction.type}
            initial={{
              description: transaction.description,
              categoryId: transaction.categoryId,
              date: transaction.date,
              amount: transaction.amount,
              spreadDays: transaction.spreadDays,
            }}
            doneLabel="Update"
            onSave={(draft) => {
              onUpdate(transaction.id, draft);
              onClose();
            }}
          />

          {receipt && receiptOpen ? (
            <button
              type="button"
              onClick={() => setReceiptOpen(false)}
              className="fixed inset-0 z-[60] bg-black/90 inline-flex items-center justify-center"
              aria-label="Close receipt viewer"
            >
              <img
                src={receipt.thumbB64}
                alt="Receipt"
                className="max-w-[92vw] max-h-[88vh] object-contain"
              />
              <span className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 inline-flex items-center justify-center text-white">
                <X size={18} strokeWidth={2} />
              </span>
            </button>
          ) : null}
        </>
      ) : null}
    </FullScreenModal>
  );
}

